import { useEffect, useState } from "react";
import { Loader2, MailCheck, Send, RotateCcw, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { appInboxAckDefaults, appInboxAckPreview, appInboxAckTest, appInboxSendAck, appInboxSetSettings } from "@/lib/api";

const err = (e, fb) => e?.response?.data?.detail || fb;

// ── Settings section (Filters & Intelligence dialog) ──────────────────────
export function AckSettings({ settings, onSaved, me }) {
  const [enabled, setEnabled] = useState(!!settings?.ack_enabled);
  const [templates, setTemplates] = useState(settings?.ack_templates || {});
  const [defaults, setDefaults] = useState(null);
  const [open, setOpen] = useState(null);
  const [saving, setSaving] = useState(null);
  const [testTo, setTestTo] = useState(me?.email || "");

  useEffect(() => { setEnabled(!!settings?.ack_enabled); setTemplates(settings?.ack_templates || {}); }, [settings]);
  useEffect(() => { appInboxAckDefaults().then(setDefaults).catch(() => {}); }, []);

  const save = async (patch, label) => {
    setSaving(label);
    try { const r = await appInboxSetSettings(patch); onSaved?.(r); if (label !== "silent") toast.success("Acknowledgement settings saved"); }
    catch (e) { toast.error(err(e, "Save failed")); } finally { setSaving(null); }
  };
  const updTpl = (intent, patch) => setTemplates((t) => ({ ...t, [intent]: { ...t[intent], ...patch } }));
  const saveTpl = (intent) => save({ ack_templates: { [intent]: templates[intent] } }, intent);
  const toggleTpl = (intent, v) => { updTpl(intent, { enabled: v }); save({ ack_templates: { [intent]: { ...templates[intent], enabled: v } } }, "silent"); };
  const reset = (intent) => { const d = defaults?.defaults?.[intent]; if (d) updTpl(intent, { subject: d.subject, body: d.body }); };
  const test = async (intent) => {
    if (!testTo.includes("@")) { toast.error("Enter an email to send the test to"); return; }
    setSaving("test-" + intent);
    try { await appInboxAckTest({ intent, to: testTo, subject: templates[intent]?.subject, body: templates[intent]?.body }); toast.success(`Test sent to ${testTo}`); }
    catch (e) { toast.error(err(e, "Test failed")); } finally { setSaving(null); }
  };
  const intents = defaults?.intents || Object.keys(templates);
  const onCount = intents.filter((i) => templates[i]?.enabled).length;

  return (
    <div className="border-t pt-4 space-y-3" data-testid="ack-settings-section">
      <div className="flex items-center justify-between gap-4 border rounded-md px-3 py-2 bg-sky-50/60 border-sky-200" data-testid="ack-master-row">
        <div>
          <div className="text-xs font-semibold flex items-center gap-1.5"><MailCheck className="w-3.5 h-3.5 text-sky-700" />Send acknowledgement email</div>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            After Autopilot (or a human approval) actions an email, reply to the sender from the applications mailbox with the template for its intent.
            Per region · reply to sender only · never for duplicates, bounces or our own addresses · one per email.
            {onCount > 0 && <> <b>{onCount}</b> intent{onCount > 1 ? "s" : ""} switched on.</>}
          </p>
        </div>
        <Switch checked={enabled} onCheckedChange={(v) => { setEnabled(v); save({ ack_enabled: v }, "silent"); }} data-testid="ack-master-switch" />
      </div>
      <div className={`space-y-1.5 ${enabled ? "" : "opacity-60"}`}>
        {intents.map((intent) => {
          const t = templates[intent] || {}; const isOpen = open === intent;
          return (
            <div key={intent} className="border rounded-md" data-testid={`ack-row-${intent.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
              <div className="flex items-center gap-2 px-2 py-1.5">
                <button onClick={() => setOpen(isOpen ? null : intent)} className="text-slate-500" data-testid={`ack-expand-${intent.replace(/[^a-z]/gi, "-").toLowerCase()}`}>{isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}</button>
                <span className="text-xs font-medium flex-1">{intent}</span>
                {t.enabled && <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-300">on</Badge>}
                <Switch checked={!!t.enabled} onCheckedChange={(v) => toggleTpl(intent, v)} data-testid={`ack-switch-${intent.replace(/[^a-z]/gi, "-").toLowerCase()}`} />
              </div>
              {isOpen && (
                <div className="px-2 pb-2 space-y-2 border-t pt-2 bg-slate-50/60">
                  <Input value={t.subject || ""} onChange={(e) => updTpl(intent, { subject: e.target.value })} className="h-7 text-xs" placeholder="Subject" data-testid="ack-subject-input" />
                  <Textarea value={t.body || ""} onChange={(e) => updTpl(intent, { body: e.target.value })} rows={7} className="text-xs" data-testid="ack-body-input" />
                  <p className="text-[10px] text-muted-foreground">Placeholders: {(defaults?.placeholders || []).join(" ")}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" className="h-7 text-[11px]" onClick={() => saveTpl(intent)} disabled={saving === intent} data-testid="ack-save-btn">{saving === intent ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}Save template</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => reset(intent)} data-testid="ack-reset-btn"><RotateCcw className="w-3 h-3 mr-1" />Reset to default</Button>
                    <span className="ml-auto flex items-center gap-1">
                      <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@…" className="h-7 text-[11px] w-48" data-testid="ack-test-to-input" />
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => test(intent)} disabled={saving === "test-" + intent} data-testid="ack-test-btn"><Send className="w-3 h-3 mr-1" />Send me a test</Button>
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Manual "Send acknowledgement" dialog (reading pane) ───────────────────
export function AckDialog({ email, intent, me, onClose, onSent }) {
  const [pv, setPv] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [err_, setErr] = useState(null);
  useEffect(() => {
    appInboxAckPreview(email.id, intent || "New App").then((r) => { setPv(r); setSubject(r.subject); setBody(r.body); })
      .catch((e) => setErr(err(e, "Could not build the acknowledgement")));
  }, [email.id, intent]);
  const send = async () => {
    setSending(true);
    try { const r = await appInboxSendAck(email.id, { intent: intent || "New App", subject, body, actor: me?.name || me?.email || "Ops" }); toast.success(`Acknowledgement sent to ${r.to}`); onSent?.(); onClose(); }
    catch (e) { toast.error(err(e, "Send failed")); } finally { setSending(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" aria-describedby={undefined} data-testid="ack-dialog">
        <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><MailCheck className="w-4 h-4 text-sky-700" />Send acknowledgement</DialogTitle></DialogHeader>
        {err_ && <p className="text-xs text-red-700" data-testid="ack-dialog-error">{err_}</p>}
        {!pv && !err_ && <p className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Building from the {intent || "New App"} template…</p>}
        {pv && (
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground border rounded px-2 py-1.5 bg-slate-50"><b>To:</b> {pv.to} · template <b>{pv.intent}</b>{!pv.enabled && <span className="ml-1 text-amber-700">(auto-send is off for this intent — this is a one-off)</span>}</div>
            {pv.already_sent && <p className="text-xs text-amber-800 border border-amber-300 bg-amber-50 rounded px-2 py-1" data-testid="ack-already-sent">An acknowledgement was already sent for this email on {new Date(pv.already_sent.posted_at).toLocaleString("en-GB")} by {pv.already_sent.actor}.</p>}
            {pv.skip_reason && <p className="text-xs text-red-700" data-testid="ack-skip-reason">Won't send: {pv.skip_reason}</p>}
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-8 text-sm" data-testid="ack-dialog-subject" />
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} className="text-sm" data-testid="ack-dialog-body" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={!pv || sending || !!pv?.skip_reason || !!pv?.already_sent || !body.trim()} data-testid="ack-dialog-send-btn">{sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Send</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
